import { Grid, MenuItem, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import React from 'react';
import moment from 'moment';
import { assessmentTypeList } from 'src/utils/mdsConstants';
import { getNursingDisplayValue } from 'src/utils/mdsConstants';
import { ArdBounds, isArdDateDisabled, isFiveDayArdWindowError } from 'src/utils/fiveDayArdWindow';

type Props = {
  formik: any;
  slpList: any;
  selectedPatient: any;
  caseMixGroup: any;
  ptOtList: any;
  /** THIN-8151: the same picker edges the initial ARD gets. */
  ardBounds: ArdBounds;
};

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: '250px',
      width: 300,
    },
  },
};

const ntaCaseMixGroup = ['NA', 'NB', 'NC', 'ND', 'NE', 'NF'];

const FinalBasicDetailsMdsAssessment = (props: Props) => {
  const { formik, slpList, caseMixGroup, ptOtList, ardBounds, selectedPatient } = props;
  const finalDateError = formik.errors?.finalMds?.assessmentDate;
  // The 5-Day window message shows as soon as it applies; other errors wait for a touch, as before.
  const showFinalDateError =
    !!finalDateError &&
    (formik.touched?.finalMds?.assessmentDate || isFiveDayArdWindowError(finalDateError));
  return (
    <>
      <Typography variant="h6">Final Assessment</Typography>
      {/* <Typography variant="subtitle1">Basic Details</Typography> */}

      <Grid
        spacing={3}
        container
      >
        <Grid
          item
          xs={6}
        >
          <DatePicker
            onChange={(newDate) => formik.setFieldValue('finalMds.assessmentDate', newDate)}
            label="Assessment Reference Date"
            value={formik.values?.finalMds?.assessmentDate}
            format="MMM dd, yyyy"
            minDate={ardBounds.minDate ? moment(ardBounds.minDate).toDate() : undefined}
            maxDate={ardBounds.maxDate ? moment(ardBounds.maxDate).toDate() : undefined}
            shouldDisableDate={(day) => isArdDateDisabled(day, ardBounds)}
            sx={{
              width: '100%',
              border: showFinalDateError ? '2px solid red' : '',
              borderRadius: showFinalDateError ? '10px' : '',
            }}
          />
          {showFinalDateError && (
            <Typography
              padding={'0px 16px'}
              color={'red'}
              variant="caption"
            >
              {finalDateError}
            </Typography>
          )}
        </Grid>
        <Grid
          item
          xs={6}
        >
          <TextField
            // defaultValue={'COMMERCIAL_INSURANCE'}
            fullWidth
            error={
              !!(
                formik.touched?.finalMds?.assessmentType && formik?.errors?.finalMds?.assessmentType
              )
            }
            helperText={
              formik.touched?.finalMds?.assessmentType &&
              (formik.errors?.finalMds?.assessmentType as any)
            }
            name="finalMds.assessmentType"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            SelectProps={{
              MenuProps: MenuProps,
            }}
            value={formik.values?.finalMds?.assessmentType}
            label="Assessment Type"
            select
            disabled={true}
          >
            {assessmentTypeList?.map((list: any) => (
              <MenuItem
                key={list}
                value={list}
              >
                {list}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
      {/* <Typography variant="subtitle1">Case-mix Group - Final</Typography> */}
      <Grid
        spacing={3}
        container
      >
        <Grid
          item
          xs={6}
        >
          <TextField
            defaultValue={'COMMERCIAL_INSURANCE'}
            fullWidth
            error={
              !!(formik.touched?.finalMds?.mdsPtOtGroup && formik.errors?.finalMds?.mdsPtOtGroup)
            }
            helperText={
              formik.touched?.finalMds?.mdsPtOtGroup &&
              (formik.errors?.finalMds?.mdsPtOtGroup as any)
            }
            name="finalMds.mdsPtOtGroup"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            SelectProps={{
              MenuProps: MenuProps,
            }}
            value={
              selectedPatient?.isNonTherapy
                ? 'Not Applicable'
                : formik.values?.finalMds?.mdsPtOtGroup
            }
            disabled={selectedPatient?.isNonTherapy}
            label="PT & OT CMG"
            select
          >
            <MenuItem
              key={'Not Applicable'}
              value={'Not Applicable'}
            >
              Not Applicable
            </MenuItem>
            {!selectedPatient?.isNonTherapy &&
              ptOtList?.map((list: any) => (
                <MenuItem
                  key={list}
                  value={list}
                >
                  {list}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid
          item
          xs={6}
        >
          <TextField
            defaultValue={'COMMERCIAL_INSURANCE'}
            fullWidth
            label="SLP CMG"
            error={
              !!(formik.touched?.finalMds?.mdsSlpGroup && formik.errors?.finalMds?.mdsSlpGroup)
            }
            helperText={
              formik.touched?.finalMds?.mdsSlpGroup && (formik.errors?.finalMds?.mdsSlpGroup as any)
            }
            SelectProps={{
              MenuProps: MenuProps,
            }}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={
              selectedPatient?.isNonTherapy
                ? 'Not Applicable'
                : formik.values?.finalMds?.mdsSlpGroup
            }
            disabled={selectedPatient?.isNonTherapy}
            name="finalMds.mdsSlpGroup"
            select
          >
            <MenuItem
              key={'Not Applicable'}
              value={'Not Applicable'}
            >
              Not Applicable
            </MenuItem>
            {!selectedPatient?.isNonTherapy &&
              slpList?.map((list: any) => (
                <MenuItem
                  key={list}
                  value={list}
                >
                  {list}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid
          item
          xs={6}
        >
          <TextField
            defaultValue={'COMMERCIAL_INSURANCE'}
            fullWidth
            label="Nursing CMG"
            error={
              !!(
                formik.touched?.finalMds?.mdsNursingGroup &&
                formik.errors?.finalMds?.mdsNursingGroup
              )
            }
            helperText={
              formik.touched?.finalMds?.mdsNursingGroup &&
              (formik.errors?.finalMds?.mdsNursingGroup as any)
            }
            name="finalMds.mdsNursingGroup"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values?.finalMds?.mdsNursingGroup}
            SelectProps={{
              MenuProps: MenuProps,
            }}
            select
          >
            <MenuItem
              key={'Not Applicable'}
              value={'Not Applicable'}
            >
              Not Applicable
            </MenuItem>
            {caseMixGroup?.map((value: any) => (
              <MenuItem
                key={value}
                value={getNursingDisplayValue(value)}
              >
                {getNursingDisplayValue(value)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid
          item
          xs={6}
        >
          <TextField
            defaultValue={'COMMERCIAL_INSURANCE'}
            fullWidth
            label="NTA CMG"
            name="finalMds.mdsNtaGroup"
            error={
              !!(formik.touched?.finalMds?.mdsNtaGroup && formik.errors?.finalMds?.mdsNtaGroup)
            }
            helperText={
              formik.touched?.finalMds?.mdsNtaGroup && (formik.errors?.finalMds?.mdsNtaGroup as any)
            }
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values?.finalMds?.mdsNtaGroup}
            select
          >
            <MenuItem
              key={'Not Applicable'}
              value={'Not Applicable'}
            >
              Not Applicable
            </MenuItem>
            {ntaCaseMixGroup?.map((value) => (
              <MenuItem
                key={value}
                value={value}
              >
                {value}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </>
  );
};

export default FinalBasicDetailsMdsAssessment;
